import _ from 'lodash';

import { documentReturnMethodOptions, signingMethodOptions } from 'constants/loanActionEditor';

import { loanManagementTestIds } from '../../loanManagement.testIds';
import { findOptionByValue } from '../LoanActionSelectOption.utils';

import {
  COMPANY_DEFAULT_PLACEHOLDER,
  EMPTY_DOCUMENT_TYPES,
  MULTILINE_ROWS,
  promptAtExecutionLabelByActionType,
  signingMethodCopy,
  SnapdocsFieldEnum,
} from './LoanActionSnapdocsRow.constants';

export const getSnapdocsSelectConfigs = ({
  rootPath,
  fields,
  snapdocsClosing,
  documentTypesOptions,
  validationErrors,
}) => {
  const isSigningMethodRequired = fields.includes(SnapdocsFieldEnum.TARGET_SIGNING_METHOD);
  const signingMethodField = isSigningMethodRequired
    ? SnapdocsFieldEnum.TARGET_SIGNING_METHOD
    : SnapdocsFieldEnum.SIGNING_METHOD;
  const { label: signingMethodLabel, placeholder: signingMethodPlaceholder } =
    isSigningMethodRequired ? signingMethodCopy.required : signingMethodCopy.optional;

  return [
    {
      field: signingMethodField,
      label: signingMethodLabel,
      placeholder: signingMethodPlaceholder,
      isMultiple: false,
      isRequired: isSigningMethodRequired,
      isHidden: !isSigningMethodRequired && !fields.includes(SnapdocsFieldEnum.SIGNING_METHOD),
      options: signingMethodOptions,
      value: findOptionByValue(signingMethodOptions, snapdocsClosing[signingMethodField]?.value),
      emptyValue: null,
      dataTestId: loanManagementTestIds.getSnapdocsSigningMethodSelectId(rootPath),
    },
    {
      field: SnapdocsFieldEnum.DOCUMENT_TYPES,
      label: 'Document Types',
      placeholder: 'All loan documents',
      isMultiple: true,
      isRequired: false,
      isHidden: !fields.includes(SnapdocsFieldEnum.DOCUMENT_TYPES),
      options: documentTypesOptions,
      value: snapdocsClosing.documentTypes ?? EMPTY_DOCUMENT_TYPES,
      emptyValue: EMPTY_DOCUMENT_TYPES,
      dataTestId: loanManagementTestIds.getSnapdocsDocumentTypesSelectId(rootPath),
    },
    {
      field: SnapdocsFieldEnum.DOCUMENT_RETURN_METHOD,
      label: 'Document Return Method',
      placeholder: 'SnapDocs default',
      isMultiple: false,
      isRequired: false,
      isHidden: !fields.includes(SnapdocsFieldEnum.DOCUMENT_RETURN_METHOD),
      options: documentReturnMethodOptions,
      value: findOptionByValue(
        documentReturnMethodOptions,
        snapdocsClosing.documentReturnMethod?.value,
      ),
      emptyValue: null,
      dataTestId: loanManagementTestIds.getSnapdocsDocumentReturnMethodSelectId(rootPath),
    },
  ].map(config => {
    const path = `${rootPath}.${config.field}`;
    const errorMessage = _.get(validationErrors, path);

    return { ...config, path, error: !!errorMessage, helperText: errorMessage };
  });
};

export const snapdocsTextFieldConfigs = [
  {
    field: SnapdocsFieldEnum.BRANDING_KEY,
    label: 'Branding Key',
    placeholder: COMPANY_DEFAULT_PLACEHOLDER,
    isMultiline: false,
    isRequired: false,
    getTestId: loanManagementTestIds.getSnapdocsBrandingKeyInputId,
  },
  {
    field: SnapdocsFieldEnum.INSTRUCTIONS,
    label: 'Notary Instructions',
    isMultiline: true,
    rows: MULTILINE_ROWS,
    isRequired: false,
    getTestId: loanManagementTestIds.getSnapdocsInstructionsInputId,
  },
  {
    field: SnapdocsFieldEnum.TEXT,
    label: 'Comment',
    isMultiline: true,
    rows: MULTILINE_ROWS,
    isRequired: true,
    isPromptable: true,
    getTestId: loanManagementTestIds.getSnapdocsCommentTextInputId,
  },
  {
    field: SnapdocsFieldEnum.REASON,
    label: 'Opt-out Reason',
    isMultiline: false,
    isRequired: false,
    isPromptable: true,
    getTestId: loanManagementTestIds.getSnapdocsOptOutReasonInputId,
  },
];

export const getSnapdocsCheckboxConfigs = itemType => [
  {
    field: SnapdocsFieldEnum.PROMPT_AT_EXECUTION,
    label: promptAtExecutionLabelByActionType[itemType],
    getTestId: loanManagementTestIds.getSnapdocsPromptAtExecutionCheckboxId,
  },
  {
    field: SnapdocsFieldEnum.SCANBACKS_REQUIRED,
    label: 'Scanbacks required',
    getTestId: loanManagementTestIds.getSnapdocsScanbacksRequiredCheckboxId,
  },
  {
    field: SnapdocsFieldEnum.SETTLEMENT_AGENT_VISIBLE,
    label: 'Visible to the settlement agent',
    getTestId: loanManagementTestIds.getSnapdocsSettlementAgentVisibleCheckboxId,
  },
  {
    field: SnapdocsFieldEnum.CONSUMER_VISIBLE,
    label: 'Visible to borrowers',
    getTestId: loanManagementTestIds.getSnapdocsConsumerVisibleCheckboxId,
  },
];
